export const auth = {
  errors: {
    deleteAccountFailed: "Your account wasn’t deleted. Try again.",
    workspacePreparing: "Your workspace is still being set up. Wait a moment, then try again.",
    signInAgain: "Sign in again to continue.",
    callbackCancelled: "Sign-in was cancelled before it finished. Try again when you’re ready.",
    signInNotFinished: "Sign-in didn’t finish. Try again.",
    signupUnavailable: "We can’t create accounts right now. Try again later.",
    signupClosed: "New accounts are by invitation for now. Use the link in your invite email, or sign in with the account you have.",
    linkExpired: "This link has expired or was already used. Ask for a new one.",
    emailInvalid: "Enter a valid email address.",
    passwordTooCommon: "That password is too easy to guess. Choose a different one.",
    passwordNeedsMix: "Use upper and lower case letters, numbers and symbols.",
    passwordTooShort: "Use at least {{n}} characters.",
    samePassword: "Choose a password you haven’t used before.",
    emailRateLimited: "We just sent you an email. Wait a minute before asking for another.",
    rateLimited: "Too many tries. Wait a minute, then try again.",
    alreadyRegistered: "This email already has an account. Tap Sign in, or Forgot your password? if you need a new one.",
    emailNotConfirmed: "Confirm your email first. Open the link we sent you, then sign in.",
    invalidCredentials: "That email and password don’t match. Check both, then try again.",
    offline: "You’re offline. Check your connection, then try again.",
    generic: "Something went wrong. Try again.",
  },
  session: {
    signIn: "Sign in",
    renew: "Renew session",
    expiredBody: "Tap Renew session to keep working, or Sign in to start again.",
    expiredTitle: "Your session timed out",
  },
  appName: 'AssetWRX',
  social: {
    errorStartFailed: "Sign-in didn’t start. Try again.",
    errorPopupTimeout: "The sign-in window waited too long and closed. Tap the sign-in button to start again.",
    errorPopupBlocked: "Your browser blocked the sign-in window. Allow pop-ups for this page, then tap the sign-in button again.",
    continueWith: 'or continue with',
    google: 'Continue with Google',
    apple: 'Continue with Apple',
    microsoft: 'Continue with Microsoft',
    signingIn: 'Opening sign-in...',
    errorSignInFailed: "Sign-in did not finish. Try again.",
  },
  callback: {
    continueHere: "Continue in this browser",
    appSignInHint: "In the app, tap Sign in and use the email and password you just chose.",
    title: "Signing you in",
    body: "One moment.",
    successTitle: "You’re signed in",
    successBody: "Opening AssetWRX…",
    errorTitle: "Sign-in didn’t finish",
    error: "This link has expired or was already used. Tap Back to sign in and try again.",
    backToSignIn: "Back to sign in",
    openInApp: "Open the AssetWRX app",
  },
  login: {
    biometricPasswordChanged: "Your password has changed, so {{method}} is off. Sign in with your new password and tick Use {{method}} next time.",
    title: "Sign in to AssetWRX",
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    subtitle: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@example.com',
    passwordPlaceholder: 'Your password',
    forgotPassword: "Forgot your password?",
    signIn: "Sign in",
    signingIn: 'Signing in...',
    noAccount: "Don't have an account?",
    createAccount: "Create account",
    createAccountCta: 'New here? Create an account',
    orWithEmail: "or sign in with email",
    newHere: "New to AssetWRX?",
    createAccountLink: "Create an account",
    biometricUseNextTime: "Use {{method}} next time",
    biometricSignIn: "Sign in with {{method}}",
    biometricPrompt: "Sign in to AssetWRX",
    biometricMethodFace: "Face ID",
    biometricMethodFingerprint: "fingerprint",
    biometricMethodIris: "iris",
    biometricMethodGeneric: "biometrics",
    embedSsoHint: "This portal signs you in with your organization's Microsoft account.",
    errorEmailRequired: 'Please enter your email.',
    errorEmailInvalid: 'Enter a valid email address.',
    errorPasswordRequired: 'Please enter your password.',
    errorLoginFailed: "Sign-in didn’t work. Check your email and password, then try again.",
  },
  signup: {
    passwordHint: "Use at least {{n}} characters.",
    useDifferentEmail: "Use a different email",
    title: "Create account",
    subtitle: 'Join AssetWRX — the operating system for your physical assets.',
    firstName: 'First Name',
    firstNamePlaceholder: 'John',
    lastName: 'Last Name',
    lastNamePlaceholder: 'Smith',
    role: 'Role',
    rolePlaceholder: 'Operations Manager',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    passwordPlaceholder: 'Choose a strong password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    confirmPassword: 'Confirm Password',
    confirmPlaceholder: 'Re-enter password',
    submit: "Create account",
    signingUp: "Creating account…",
    hasAccount: 'Already have an account?',
    signInLink: "Sign in",
    emailInstead: 'Sign up with email instead',
    emailInsteadHide: 'Hide email sign-up',
    useEmail: "Use email instead",
    useEmailHide: "Hide email sign-up",
    name: "Your name",
    namePlaceholder: "Alex Taylor",
    errorNameRequired: "Please enter your name.",
    hasAccountShort: "Already have an account?",
    errorFirstNameRequired: 'Please enter your first name.',
    errorLastNameRequired: 'Please enter your last name.',
    errorRoleRequired: 'Please enter your role.',
    errorEmailRequired: 'Please enter your email.',
    errorPasswordRequired: 'Please enter a password.',
    errorPasswordMismatch: 'Passwords do not match.',
    emailInvalid: 'Enter a valid email address.',
    passwordsMatch: 'Passwords match.',
    passwordStrength: {
      weak: 'Weak',
      fair: 'Fair',
      good: 'Good',
      strong: 'Strong',
    },
    errorSignUpFailed: "Your account wasn’t created. Try again.",
    errorLanguageInvalid: 'Choose a supported language.',
    errorTimezoneInvalid: 'Choose a valid time zone.',
    regionalSectionTitle: 'Language, time zone & currency',
    regionalSectionBody:
      'These apply to your profile and your new workspace. You can change them later in settings.',
    languageLabel: 'Language',
    timezoneLabel: 'Time zone',
    currencyLabel: 'Workspace currency',
    currencyA11y: 'Workspace currency {{code}}',
    successCheckEmail:
      'Account created. Please check your email to confirm your account, then sign in.',
    successEmailTitle: 'Check your inbox',
    successEmailBody: "We sent a link to {{email}}. Open it to finish creating your account.",
    resendConfirmation: "Send the link again",
    resendingConfirmation: "Sending…",
    resendSuccess: "Sent. Check your inbox and spam folder.",
    resendError: "The link didn’t send. Wait a minute, then tap Send the link again.",
    consent: {
      sectionTitle: 'Before you continue',
      sectionBody: 'Please review and accept the documents below. Both are required to create your account.',
      acceptTerms: 'I have read and accept the <1>Terms of Service</1>.',
      acceptPrivacy: 'I have read and accept the <1>Privacy Policy</1>.',
      errorTermsRequired: 'Please accept the Terms of Service to continue.',
      errorPrivacyRequired: 'Please accept the Privacy Policy to continue.',
      a11yTermsCheckbox: 'Accept Terms of Service',
      a11yPrivacyCheckbox: 'Accept Privacy Policy',
      compactLead: 'I accept:',
      termsShort: 'Terms',
      read: 'Read',
      privacyShort: 'Privacy',
      a11yReadTerms: 'Read the Terms of Service',
      a11yReadPrivacy: 'Read the Privacy Policy',
      shareUsage: 'Share usage data to help improve AssetWRX (optional)',
      byContinuingBefore: "By continuing you agree to the ",
      byContinuingBetween: " and ",
      byContinuingAfter: ".",
      termsDoc: "Terms of Service",
      privacyDoc: "Privacy Policy",
    },
    wizard: {
      flowSubtitle: 'A quick guided setup for your workspace',
      valueStrip: 'Start free. No card needed.',
      stepProgress: 'Step {{current}} of {{total}}',
      loadingDraft: 'Restoring your progress…',
      welcomeBody: 'Create your account with Google, Apple, Microsoft, or email. New accounts go through a short setup after sign-in.',
      continueWithEmail: 'Continue with email',
      back: 'Back',
      next: 'Next',
      yes: 'Yes',
      no: 'No',
      errorUseCaseRequired: 'Please choose how you plan to use AssetWRX.',
      errorWorkspaceNameRequired: 'Please enter a workspace name.',
      errorStarterRequired: 'Choose what you want to do first.',
      useCaseTitle: 'Primary use case',
      useCaseBody: 'Pick the option that best describes you. You can refine this later.',
      useCases: {
        field_inspections: "Inspecting places or equipment",
        asset_register: "Keeping a record of what we look after",
        jobs_workorders: "Organising repairs and jobs",
        team_collab: "Working with my team",
        compliance_reporting: "Certificates and compliance",
        other: "Something else",
      },
      workspaceTitle: 'Your workspace',
      workspaceBody: 'This is your organization space in AssetWRX. You can rename it later in settings.',
      workspaceNameLabel: 'Workspace name',
      workspaceNamePlaceholder: 'e.g. Acme Facilities',
      inviteOnlyNote: 'Joining someone else’s team is by invitation only (use the link from your invite email).',
      intentCreateTeam: 'Create a team in this workspace',
      intentInviteMembers: 'Invite team members after setup',
      starterTitle: 'What do you want to do first?',
      starterBody: 'We will open the right area after you sign in and finish setup.',
      starters: {
        home: 'Open the home dashboard',
        asset: 'Add an asset',
        inspection: 'Start an inspection',
        schedule: 'Schedule an inspection',
        team: 'Manage team & invites',
      },
      reviewTitle: 'Review & create account',
      reviewName: 'Name: {{name}}',
      reviewEmail: 'Email: {{email}}',
      reviewWorkspace: 'Workspace: {{name}}',
      reviewTeam: 'Create a team: {{yes}}',
      reviewInvites: 'Invite members: {{yes}}',
      reviewEditHint:
        'This step only shows a summary. To type a different name, email, or password, use the button below (or go Back to step through earlier pages).',
      reviewEditCta: 'Edit name, email & password',
    },
  },
  firstRunSample: {
    secondCheck: {
      property: 'Smoke alarms present and working?',
      vehicle: 'Lights and tyres OK?',
      equipment: 'Guards and warning labels in place?',
      other: 'Safety equipment present and serviceable?',
    },
    quickLook: {
      templateName: 'Quick look round',
      firstCheck: 'Visual condition acceptable?',
    },
    useCases: {
      field_inspections: {
        assetName: 'Sample circulation pump',
        assetDescription: 'A removable sample for trying a field inspection.',
        templateName: 'Pump condition check',
        firstCheck: 'Housing and guards secure?',
        secondCheck: 'Any leaks, noise or vibration?',
      },
      jobs_workorders: {
        assetName: 'Sample air handling unit',
        assetDescription: 'A removable sample for finding and handing off maintenance work.',
        templateName: 'Maintenance walkthrough',
        firstCheck: 'Access and isolation points clear?',
        secondCheck: 'Any follow-up maintenance needed?',
      },
      compliance_reporting: {
        assetName: 'Sample fire-safety equipment',
        assetDescription: 'A removable sample for trying evidence-led compliance checks.',
        templateName: 'Compliance evidence check',
        firstCheck: 'Identification and condition confirmed?',
        secondCheck: 'Evidence captured and in date?',
      },
      asset_register: {
        assetName: 'Sample service vehicle',
        assetDescription: 'A removable sample showing an operational asset record.',
        templateName: 'Vehicle condition check',
        firstCheck: 'Exterior condition acceptable?',
        secondCheck: 'Safety equipment present and serviceable?',
      },
      other: {
        assetName: 'Sample equipment',
        assetDescription: 'A removable sample for trying an inspection and follow-up work.',
        templateName: 'Quick operational check',
      },
    },
  },
  postSignupOnboarding: {
    industryTitle: 'What do you manage?',
    industryHint: "We add ready-made checklists for it. You can change them later in Checklists.",
    industryChoices: {
      property: 'Property',
      fleet: 'Fleet',
      equipment: 'Equipment',
      facilities: 'Facilities',
      mixed: "A mix, or something else",
    },
    title: 'Set up your workspace',
    subtitle: 'A name and two quick choices, and you’re in.',
    stepProgress: 'Step {{current}} of {{total}}',
    workspaceTitle: 'Workspace name',
    workspacePlaceholder: 'Your organisation or team name',
    workspaceHint: "Usually your company or team name. You can change it later.",
    useCaseTitle: 'What will you use AssetWRX for?',
    roleTitle: 'Your role',
    teamTitle: 'Team & invites',
    starterTitle: 'Where should we take you first?',
    finish: 'Continue',
    finishing: 'Setting up…',
    deferNote: "You can change language, currency and team settings later in Account.",
    errorWorkspaceRequired: 'Please enter a workspace name.',
    errorUseCaseRequired: 'Please choose a primary use case.',
    errorRoleRequired: 'Please choose your role.',
    errorSave: 'Could not save. Please try again.',
    inviteNote: "You can invite people after this step.",
    applyingTitle: 'Applying your choices',
    applyingBody: "Setting up your workspace. This takes a moment.",
    languageTitle: "Language",
    localeLineTitle: "Currency and language",
    localeChange: "Change",
    localeDone: "Done",
    languageHint: "Choose your language. You can change it later in Account.",
    currencyTitle: "Currency",
    currencyHint: "Choose the currency you work in. You can change it later in Account.",
  },
  firstActionChooser: {
    sample: {
      subtitle: "We add a sample asset so you can see how an inspection works. Remove it any time.",
      title: "Try a practice inspection",
    },
    asset: {
      subtitle: "A name and a type is all you need. Add more later.",
      title: "Add my first asset",
    },
    title: "What would you like to do first?",
    framing: "Add something you look after, or try a practice inspection on a sample.",
    framingTeam: "Invite the people you work with, add something you look after, or try a practice inspection.",
    recommended: 'Suggested',
    reassure: "You can remove the sample any time.",
    sampleErrorFallback: 'Could not prepare the sample. You can add your own asset instead.',
    skip: 'Go to Home',
    team: {
      title: "Set up your team",
      subtitle: "Create a team and invite the people you work with. We show you each step.",
      subtitleLocked: "Teams come with a higher plan. Tap to see which plans include it.",
    },
  },
  forgot: {
    sentAgain: "Sent again. Check your inbox and spam folder.",
    spamHint: "Didn’t get it? Check your spam folder.",
    sendAgain: "Send it again",
    title: "Forgot your password?",
    subtitleEmail: "Enter your email and tap Send reset link. We’ll email you a link to choose a new password.",
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    sendLink: 'Send reset link',
    sending: 'Sending...',
    backToSignIn: "Back to sign in",
    sentTitle: 'Check your email',
    sentBody: "If {{email}} has an account, we’ve sent a reset link. Open it and choose a new password.",
    errorEmailRequired: 'Please enter your email.',
    errorSendFailed: "The reset link didn’t send. Wait a minute, then try again.",
  },
  reset: {
    subtitleDoneApp: "Your password is changed. Tap Sign in and use it.",
    sendNewLink: "Send a new link",
    titleExpired: "This link has expired",
    title: 'Set new password',
    titleDone: 'Password updated',
    subtitle: 'Choose a new password for your account.',
    subtitleNoSession: "Reset links work once and expire after an hour. Tap Send a new link and we’ll email you another one.",
    subtitleForm: "Type your new password twice, then tap Update password.",
    subtitleDone: "Your password is changed. Go back to the AssetWRX app and sign in with it.",
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    confirmPassword: 'Confirm password',
    passwordPlaceholder: 'Choose a strong password',
    confirmPlaceholder: 'Re-enter password',
    submit: 'Update password',
    updating: 'Updating...',
    loading: 'Loading...',
    success: 'Password updated. You can sign in with your new password.',
    goToSignIn: "Sign in",
    backToSignIn: "Back to sign in",
    errorTooShort: "Your new password is too short. Use at least {{n}} characters.",
    errorPasswordRequired: 'Please enter a new password.',
    errorMismatch: 'Passwords do not match.',
    errorUpdateFailed: 'Could not update password.',
    errorResetFailed: 'Failed to reset password.',
    errorInvalidSession: 'This reset link is invalid or has expired. Request a new one from the sign-in page.',
  },
  reconsent: {
    firstBody: "Read the Terms of Service and Privacy Policy, tick both boxes, then tap Accept and continue.",
    firstTitle: "Accept the terms to continue",
    errorMustAcceptBoth: 'Both documents must be accepted to continue.',
    errorMustAcceptOne: 'The document must be accepted to continue.',
    errorSessionExpired: 'Your session has expired. Please choose “Decline and sign out”, then sign in again to continue.',
    errorCouldNotRecord: 'Could not record your acceptance. Check your connection and try again.',
    unavailableTitle: 'Couldn’t check your saved acceptance',
    unavailableBody: 'Connect and try again to check whether any documents need your review. Your saved work remains on this device.',
    tryAgain: 'Try again',
    continueOffline: 'Continue offline for now',
    signOut: 'Sign out',
    needsConsentTitle: 'Updated terms require your acceptance',
    needsConsentBody: 'We have updated the documents below. Please review and accept both to continue using AssetWRX. If you choose not to accept, you will be signed out — you can still request account deletion or export your data later.',
    needsConsentBodyOne: 'We have updated the document below. Please review and accept it to continue using AssetWRX. If you choose not to accept, you will be signed out — you can still request account deletion or export your data later.',
    acceptTermsA11y: 'I accept the Terms of Service (v{{version}})',
    acceptTermsLabel: 'I accept the Terms of Service (v{{version}}).',
    readTermsA11y: 'Read Terms of Service',
    readTerms: 'Read Terms of Service',
    acceptPrivacyA11y: 'I accept the Privacy Policy (v{{version}})',
    acceptPrivacyLabel: 'I accept the Privacy Policy (v{{version}}).',
    readPrivacyA11y: 'Read Privacy Policy',
    readPrivacy: 'Read Privacy Policy',
    recording: 'Recording',
    recordingEllipsis: 'Recording…',
    acceptAndContinue: 'Accept and continue',
    declineAndSignOut: 'Decline and sign out',
  },
  guidedHub: {
    groupRule: {
      title: "Check a whole group of assets on one schedule",
      subtitle: "Set up a group rule, such as every fire door each month.",
    },
    assistantActivity: {
      title: "Choose who changes things through the assistant",
      subtitle: "Turn changes through the assistant on or off for members.",
    },
    requestFeature: {
      title: "Request a feature",
      subtitle: "Tell the AssetWRX team what the app should do next.",
    },
    assetToursHint: "More tours are on each asset: open it, tap +, then Guide me.",
    guide: {
      subtitle: "Step-by-step help for every part of the app.",
      title: "Read the user guide",
    },
    invite: {
      subtitle: "They get an email and join your team.",
      title: "Invite a colleague",
    },
    title: "What do you want to do?",
    subtitle: "Pick a task. We highlight each thing to tap, one step at a time.",
    close: "Close",
    groups: {
      setup: "Set up your workspace",
      reactive: "Share",
      proactive: "Plan the work",
      workspace: "Run your workspace",
    },
    building: {
      title: "Set up a building",
      subtitle: "A building, then its floors and rooms inside.",
    },
    asset: {
      title: "Add an asset",
      subtitle: "A building, vehicle, machine or anything you look after.",
    },
    site: {
      title: "Add a site",
      subtitle: "A place, like a building or depot, that holds assets.",
    },
    contractor: {
      title: "Add a contractor",
      subtitle: "A person or company you send work to.",
    },
    team: {
      title: "Create a team",
      subtitle: "Share assets and work with colleagues.",
    },
    requesters: {
      title: "Share an asset",
      subtitle: "Let someone see an asset, or ask for work on it.",
    },
    inspection: {
      title: "Schedule an inspection",
      subtitle: "One visit, or one that repeats.",
    },
    job: {
      title: "Create a job",
      subtitle: "A repair or task, and who does it.",
    },
    maintenancePlan: {
      title: "Set up a maintenance plan",
      subtitle: "Work that repeats, like a yearly service.",
    },
    compliance: {
      title: "Check certificates and compliance",
      subtitle: "See what is missing or due, and fix it.",
    },
    industryPack: {
      title: "Add ready-made checklists",
      subtitle: "Checklists made for your trade.",
    },
    transferOwnership: {
      title: "Hand the team to a new owner",
      subtitle: "They accept the offer before anything changes.",
    },
  },
  guidedJourney: {
    esg: {
      figures: {
        title: "The headline figures",
        body: "Each figure for the last 12 months with its change on the year before, from the meter readings you already record. Below it, the chart and where the carbon comes from.",
      },
      refrigerant: {
        title: "Refrigerant and F-gas",
        body: "The gas comes from the asset's Details. Record the charge; the app works out the tonnes CO2e, how often it needs a leak check, and plans the check. The leak log lists every check, leak and top-up.",
      },
      certificates: {
        title: "Energy certificates",
        body: "Certificates are kept once, under Documents. Add the EPC or DEC there and the AI reads the rating and dates; this card shows it with Open certificate and Edit.",
      },
      writeup: {
        title: "Write it up",
        body: "Pick the reader and the AI writes a short summary that only uses these figures, with buttons to raise the work it suggests.",
      },
    },
    subJobs: {
      card: {
        title: "The pieces of this job",
        body: "Each sub-job has its own person or contractor, date, checks and photos. You check each one; the whole job is signed off once.",
      },
      add: {
        title: "Tap Add sub-job",
        body: "The first time, say who runs the job. Then give the piece a title, who does it and when. Add several in a row with Add and next.",
      },
    },
    visits: {
      add: {
        title: "Plan another visit",
        body: "Pick the day and time. The job shows on Work and the calendar on each visit day, and is completed once, at the end.",
      },
    },
    groupRule: {
      add: {
        title: "Tap Add group rule",
        body: "A group rule puts one checklist on every asset in a group, on one schedule.",
      },
      which: {
        title: "Say which assets",
        body: "Pick a kind: Place, Equipment, Vehicle or Other. Then narrow it to a kind of place or one of your asset types, such as Fire door.",
      },
      where: {
        title: "Say where",
        body: "Leave it as Anywhere, or pick a place such as Building A to take only what is inside it. A zone narrows it further.",
      },
      checklist: {
        title: "Pick the checklist",
        body: "Each inspection the rule makes uses this checklist.",
      },
      howOften: {
        title: "How often and from when",
        body: "Pick how often, and the date of the first inspection. Each asset's next one is planned when it is done.",
      },
      summary: {
        title: "Check the sentence",
        body: "The rule is read back in one sentence, with how many inspections saving makes.",
      },
      save: {
        title: "Save the rule",
        body: "Tap Save rule. The inspections appear on the Work page, and assets that join the group later get theirs.",
      },
    },
    assistantActivity: {
      policy: {
        title: "Choose who may make changes",
        body: "Owners and admins pick: everyone, only admins, or nobody. With nobody, the assistant answers and advises but changes nothing.",
      },
      briefing: {
        title: "Your weekly briefing",
        body: "Turn the weekly briefing at the top of the assistant on or off, and turn suggested questions back on. This is your own setting.",
      },
      memory: {
        title: "What the assistant remembers",
        body: "Preferences for the whole workspace that the assistant follows, such as what you call things or who usually gets a kind of job. Add, change or forget them here.",
      },
      list: {
        title: "Every change is listed here",
        body: "What was changed, by whom and when, and whether it was undone. Members see their own.",
      },
    },
    requestFeature: {
      open: {
        title: "Tap Request a feature",
        body: "It is under Help, below the User guide.",
      },
      what: {
        title: "Say what you would like",
        body: "A short title, then how it would help you. Leave out personal or customer details.",
      },
      area: {
        title: "Pick the part of the app",
        body: "Choose what it is about. Say how much it matters too, and add screenshots if they help.",
      },
      send: {
        title: "Send it",
        body: "Tap Send request. It goes to the AssetWRX team, and Your requests shows its status and any reply.",
      },
    },
    inMoreMenu: "Tap More to find it.",
    myDay: {
      startNext: {
        body: "Tap Start next to open the next job and begin it.",
        title: "Start your next visit",
      },
      share: {
        body: "Tap Share location so your planner can see if you are running late. Or tap Next.",
        title: "Share your location (optional)",
      },
      agenda: {
        body: "Your visits for today, in order, with the drive between them. Tap Next.",
        title: "This is your day",
      },
    },
    inPlusMenu: "Tap + to find it.",
    next: "Next",
    skipStep: "Skip this step",
    skip: "End tour",
    share: {
      confirm: {
        body: "Pick Can view or Can request work, then tap Share. People without an account get an email invite.",
        title: "Choose what they can do",
      },
      assetOpen: {
        title: "Open the asset",
        body: "Tap the asset you want to share.",
      },
      shareAction: {
        title: "Share with a person",
        body: "Tap Share with a person or team. They get access to this asset only.",
      },
      createContact: {
        title: "Pick the person",
        body: "Tap a contact, or Create & link contact to add someone new. Then tap Continue.",
      },
    },
    inspection: {
      pickAsset: {
        title: "Pick the asset",
        body: "Tap the asset you want to inspect.",
      },
      createChecklist: {
        title: "Pick the checklist",
        body: "Keep this checklist and tap Next, or tap Change and pick one. No checklist yet? Tap Create a checklist.",
      },
      when: {
        title: "Choose when",
        body: "Tap Now to start straight away, or Later to pick a date and time.",
      },
      submit: {
        title: "Start it",
        body: "Tap Start inspection (or Schedule inspection if you chose Later). Answer each item, then tap Complete inspection.",
      },
    },
    job: {
      pickAsset: {
        title: "Pick the asset",
        body: "Tap the asset the work is for.",
      },
      title: {
        title: "Say what needs doing",
        body: "Type a short title, like Replace worn belt, then choose who does it.",
      },
      submit: {
        title: "Create the job",
        body: "Tap Create job (or Schedule job if you chose Later). Whoever you assign is told and asked to accept.",
      },
    },
    proactive: {
      when: {
        title: "Tap Later",
        body: "Then pick the day and time of the first check.",
      },
      recurrence: {
        title: "Choose how often",
        body: "Under Repeat, tap how often it comes back, like Every month.",
      },
      submit: {
        title: "Schedule it",
        body: "Tap Schedule inspection. Each check appears in Work on its day; tap Open in the message to see it.",
      },
    },
    addSite: {
      name: {
        title: "Name the site",
        body: "Type the name your team uses, like North Depot.",
      },
      location: {
        title: "Address (optional)",
        body: "Type the address and pick it from the list so people can find the site. Or tap Next to skip.",
      },
      classify: {
        title: "Type of site (optional)",
        body: "Pick what kind of place it is and its status. Or tap Next to skip.",
      },
      structure: {
        title: "Parent site and team (optional)",
        body: "Put it inside a bigger site, or choose the team that looks after it. Or tap Next to skip.",
      },
      create: {
        title: "Create the site",
        body: "Tap Create site. The site opens, ready for its assets.",
      },
    },
    addAssetsToSite: {
      assign: {
        title: "Add assets to this site",
        body: "Tap Assign asset and pick the asset to move here. A message confirms the move.",
      },
    },
    addContractor: {
      name: {
        title: "Type their name",
        body: "Enter the person’s or company’s name.",
      },
      contact: {
        title: "Type their email",
        body: "A contractor needs an email: job and quote invitations go there.",
      },
      save: {
        title: "Add them",
        body: "Tap Add contact. To give them work, open a job and tap Assign.",
      },
    },
    raiseRequest: {
      summary: {
        body: "Type a short summary, like Leaking tap in kitchen.",
        title: "Say what is wrong",
      },
      outcome: {
        title: "What do you need?",
        body: "Pick one: Just help — not sure yet, A job done, or An inspection.",
      },
      submit: {
        title: "Send it",
        body: "Tap Submit. A job or inspection opens straight away, filled in; otherwise it waits in Work.",
      },
    },
    shareReport: {
      contact: {
        title: "Pick who gets it",
        body: "Tap Select contact and pick the person.",
      },
      role: {
        title: "Check who they are",
        body: "Their relationship is filled in from the contact. Tap Change only if it is wrong, or tap Next.",
      },
      send: {
        title: "Send it",
        body: "Tap Send report. They get an email with a link, and you are told when they reply.",
      },
    },
    planADay: {
      apply: {
        body: "Nothing is saved until you tap Apply. Each person is then asked to accept their day.",
        title: "Tap Apply to save",
      },
      optimise: {
        body: "Tap Suggest a plan to fill the day with the least travel. Tap × on any change you don’t want.",
        title: "Or let the app suggest",
      },
      panel: {
        body: "Pick the person, the day and the start time. It shows dashed until you tap Apply.",
        title: "Choose who and when",
      },
      unplanned: {
        body: "This list shows work with no time or no person. Tap one to plan it.",
        title: "Start with Unplanned",
      },
      },
    convertRequest: {
      approve: {
        title: "Approve the request",
        body: "Tap Approve, then Approve again to confirm. The person who asked is told.",
      },
      convert: {
        title: "Turn it into work",
        body: "Tap Create a job or Inspect. The details are filled in; you choose who does it and when.",
      },
    },
    addDocument: {
      title: {
        body: "Type a name people will recognise, like Gas safety certificate 2026.",
        title: "Give it a title",
      },
      type: {
        title: "Choose the kind of document",
        body: "Under Kind of document, pick Certificate, Warranty, Compliance or General.",
      },
      save: {
        title: "Add the file and save",
        body: "Attach the file, add the expiry date if it has one, and tap Save document.",
      },
    },
    assignAssetTeam: {
      change: {
        title: "Tap Change",
        body: "Next to Responsible team, tap Change.",
      },
      pick: {
        title: "Pick the team",
        body: "Tap the team. A message confirms who can now see it.",
      },
    },
    addTeam: {
      open: {
        title: "Tap Create team",
        body: "A team shares assets and work with the people you invite. Already have one? Tap New team.",
      },
      name: {
        title: "Name the team",
        body: "Use a name your colleagues will recognise.",
      },
      submit: {
        title: "Create it",
        body: "Tap Create team. The invite box opens next so you can add people.",
      },
    },
    inviteMembers: {
      open: {
        title: "Tap Invite people",
        body: "Invite a colleague to this team.",
      },
      email: {
        title: "Enter their email",
        body: "Type their email address, one per line, and choose Member or Admin.",
      },
      send: {
        title: "Send the invite",
        body: "Tap Send invitation. They get an email and appear under Members once they accept.",
      },
    },
    assignContractor: {
      open: {
        title: "Tap Assign",
        body: "Pick the colleague or contractor who will do it. A contractor needs an email so they are told.",
      },
    },
    aiReport: {
      reader: {
        title: "Who is it for?",
        body: "Pick the reader. The same facts are told in the order that reader cares about. Or tap Next.",
      },
      analyses: {
        title: "Choose what to cover",
        body: "All topics are on. Tap one to leave it out, or tap Next.",
      },
      generate: {
        title: "Create the report",
        body: "Check the credit cost, then tap Write the AI version. You are told when it is ready, and credits are used only then.",
      },
    },
    relateAsset: {
      search: {
        title: "Find the other asset",
        body: "Search for the other asset and tap it.",
      },
      save: {
        title: "Save the link",
        body: "Choose how they are related and tap Add relationship.",
      },
    },
    publicIntake: {
      share: {
        title: "Tap Share",
        body: "Reporting by QR code is set up from Share.",
      },
      choose: {
        title: "Let anyone report a problem",
        body: "Tap Let anyone with the code report a problem. No account or sign-in is needed.",
      },
      enable: {
        title: "Switch it on",
        body: "Turn the switch on. The code and the link appear underneath.",
      },
      copy: {
        title: "Share the link",
        body: "Tap Copy link (or Print QR on a computer) and put it where people will see it.",
      },
    },
    setUpBuilding: {
      name: {
        title: "Name the building",
        body: "Type the name people use, like Building A or North Tower.",
      },
      type: {
        title: "It is a place",
        body: "Place is already picked. Floors, rooms, car parks and other areas are places too.",
      },
      placeType: {
        title: "Tap Building",
        body: "Say what kind of place it is. Pick Building here; later you pick Floor or Room for what goes inside.",
      },
      save: {
        title: "Create it",
        body: "Tap Create asset. The building opens next.",
      },
      addInside: {
        title: "Add its floors",
        body: "Tap Add place inside to add Floor 1. Each floor has the same button for its rooms, and Add equipment here for what is fitted in it.",
      },
    },
    addAsset: {
      name: {
        title: "Name it",
        body: "Type the name your team uses, like Boiler 3 or 14 Oak Street.",
      },
      type: {
        title: "Pick the type",
        body: "Tap Place, Equipment, Vehicle or Other. A place is a building, floor, room or any other area. If a Looks like card appears, tap Use this.",
      },
      location: {
        title: "Where is it? (optional)",
        body: "Add the site or address so people can find it. Or tap Next to skip.",
      },
      identify: {
        title: "Photo and serial (optional)",
        body: "Add a photo and the number on its plate so it can be found on site. Or tap Next to skip.",
      },
      details: {
        title: "More details (optional)",
        body: "Add make, model and key dates now or later. Or tap Next to skip.",
      },
      save: {
        title: "Create it",
        body: "Tap Create asset. It opens next, ready to inspect.",
      },
    },
    decommission: {
      options: {
        title: "Open the ⋯ menu",
        body: "Tap ⋯ at the top of the asset.",
      },
      status: {
        title: "Tap Change status",
        body: "Commission, decommission, retire and restore live here.",
      },
      choose: {
        title: "Tap Decommission",
        body: "The asset keeps its history. Routine work stops until it is back in service.",
      },
      reason: {
        title: "Choose a reason",
        body: "Pick why it is leaving service. It is saved on the record.",
      },
      confirm: {
        title: "Confirm",
        body: "Tap Raise the make-safe job to make it safe first, or switch that off and tap Take out of service now.",
      },
    },
    commission: {
      start: {
        title: "Tap Commission",
        body: "On the banner, tap Commission to bring the asset into service.",
      },
      inspect: {
        title: "Start the inspection",
        body: "Tap Start inspection. When it is done and its jobs are finished, tap Return to service on the asset.",
      },
    },
    retire: {
      options: {
        title: "Open the ⋯ menu",
        body: "Tap ⋯ at the top of the asset.",
      },
      status: {
        title: "Tap Change status",
        body: "Commission, decommission, retire and restore live here.",
      },
      choose: {
        title: "Tap Retire asset",
        body: "Only an asset that is out of service can be retired. If not, you are offered Decommission first.",
      },
      reason: {
        title: "Say why",
        body: "Pick the reason. If it was sold or scrapped, add who took it.",
      },
      confirm: {
        title: "Type RETIRE to confirm",
        body: "Type RETIRE, then tap Retire this asset. An owner or admin can restore it later.",
      },
    },
    meterReading: {
      value: {
        title: "Enter the reading",
        body: "Type the number on the meter. On a phone you can add a photo instead and the app reads it.",
      },
      save: {
        title: "Save it",
        body: "Tap Save reading. Plans that count miles or hours start from this number.",
      },
    },
    maintenancePlan: {
      asset: {
        body: "Tap Asset and pick what this plan is for. Already set? Tap Next.",
        title: "Choose the asset",
      },
      title: {
        title: "Say what gets done",
        body: "Under What gets done, type the work each time, like Annual service.",
      },
      cadence: {
        title: "Choose how often",
        body: "Under When is it due?, keep Repeats and set Every 12 months, or turn on Repeat by meter. Then tap Next.",
      },
      save: {
        title: "Create the plan",
        body: "Choose Job or Inspection under Do it as, then tap Create plan. A message says when the first one appears in Work.",
      },
    },
    addKnowledge: {
      kind: {
        title: "Choose what to add",
        body: "Pick a note, FAQ, how-to, link or file.",
      },
      title: {
        title: "Give it a title",
        body: "Use words people would search for, like Boiler reset. Then write or dictate it.",
      },
      save: {
        title: "Save it",
        body: "Tap Save. Anyone working on this asset can find it.",
      },
    },
    moveAsset: {
      reason: {
        title: "Moved, or a correction?",
        body: "Tap It moved or It was wrong.",
      },
      location: {
        title: "Where is it now?",
        body: "Search for the address, or use your current location.",
      },
      submit: {
        title: "Save the move",
        body: "Tap Record the move, or Correct the location. The old place stays in the history.",
      },
    },
    complianceRegister: {
      refine: {
        title: "Find what you need (optional)",
        body: "Search by name, or tap Refine to filter. Then tap Next.",
      },
      row: {
        title: "Fix a row",
        body: "Each row says what is missing or due. Tap its button, like Renew or Add expiry date.",
      },
    },
    industryPack: {
      install: {
        title: "Install a pack",
        body: "Tap Install pack on the one for your trade. Its checklists appear in Checklists.",
      },
    },
    transferOwnership: {
      open: {
        title: "Tap Transfer ownership",
        body: "It is under Team settings. Only the owner sees it, once the team has another member.",
      },
      member: {
        title: "Choose the new owner",
        body: "Pick a member. Nothing changes until they accept; then you become an admin.",
      },
    },
  },
  mfa: {
    codeNotValid: "That code didn’t work. Codes change every 30 seconds, so open your authenticator app and type the newest one.",
    invalidCode: "That code was not valid. Check your authenticator app and try again.",
    enterCode: "Enter the 6-digit code",
    verifying: "Verifying…",
    verifyCta: "Verify and turn on",
    challenge: {
      contactSupport: "Contact AssetWRX support",
      pageTitle: "Two-factor verification",
      title: "Enter your verification code",
      body: "Open your authenticator app and enter the 6-digit code for AssetWRX.",
      backA11y: "Cancel sign-in and go back",
      back: "Back",
      loadError: "Could not load your two-factor method. Sign in again.",
      resetSent_one: "We let your workspace admin know. They can reset your two-factor so you can set it up again.",
      resetSent_other: "We let your workspace admins know. They can reset your two-factor so you can set it up again.",
      resetNoAdmin: "You're the workspace owner, so there's no one above you to reset this. Sign in on a device that still has your authenticator, or contact AssetWRX support below.",
      resetError: "Could not send the request. Please try again.",
      verifyCta: "Verify",
      lostDevice: "Lost your device? Ask a workspace admin to reset your two-factor setup, or sign out.",
      requestReset: "Request a reset from your admin",
      requesting: "Sending…",
      signOut: "Sign out",
    },
    enroll: {
      otherPhoneQr: "Using another phone? Scan this code.",
      step2SameDevice: "Tap Open authenticator app, add the account, then come back here",
      a11yGooglePlay: "{{app}}: get it on Google Play",
      a11yAppStore: "{{app}}: download on the App Store",
      appBlurb: "Free. Works on iPhone and Android.",
      backA11y: "Go back",
      back: "Back",
      title: "Set up two-factor sign-in",
      lead: "This adds a second step at sign-in so only you can get in — even if someone learns your password. It takes about a minute.",
      startError: "Could not start setup. Please try again.",
      step1: "Get a free authenticator app on your phone",
      step1Hint: "Already have one? Skip to step 2.",
      appStore: "iPhone",
      googlePlay: "Android",
      step2: "In the app, tap \"＋\" and scan this code",
      qrAlt: "Two-factor QR code",
      manualLabel: "Can't scan? Enter this key manually:",
      copySecret: "Copy setup key",
      openApp: "Open authenticator app",
      step3: "Enter the 6-digit code the app shows",
      preparing: "Preparing your setup…",
      backupTitle: "Add a backup, just in case",
      backupBody: "Two-factor is on. If you ever lose your authenticator, a second one on another device is the fastest way back in. Recommended.",
      addBackup: "Add a backup authenticator",
      finish: "Finish",
      backupSetupTitle: "Add your backup authenticator",
      backupSetupBody: "Scan this with your second authenticator app, then enter the 6-digit code it shows.",
      skipBackup: "Skip for now",
    },
    gate: {
      challengeTitle: "Verify it’s you",
      blockedTitle: "Two-factor sign-in is required",
      nagTitle: "Turn on two-factor sign-in",
      challengeBody: "Enter your authenticator code to continue.",
      blockedBody: "To keep your account secure, set up an authenticator app to continue.",
      nagBody: "Add an authenticator app for a big security boost — it only takes a minute.",
      nagBodyDated: "Two-factor sign-in becomes required on {{dateLabel}}. Set it up now — it only takes a minute.",
      verifyCta: "Enter code",
      setupCta: "Set up two-factor",
      signOut: "Sign out",
      later: "Remind me later",
    },
  },
} as const;
